import { useState } from "react";
import PublicShell, { Block, SampleNote } from "@/components/PublicShell";
import useSeo from "@/hooks/useSeo";

const SAMPLE_LEADS = [
  { name: "Anita Rao", status: "New", source: "Landing page", note: "Asked about Saturday openings", consent: "No call consent" },
  { name: "James Wright", status: "Booked", source: "Google Ads", note: "Consultation Fri 4:00 pm", consent: "Consent recorded" },
  { name: "Priya Shah", status: "Converted", source: "CSV import", note: "First visit completed", consent: "Consent recorded" },
  { name: "Marcus Lee", status: "New", source: "Meta Lead Ad", note: "Wants pricing info", consent: "Do not call" },
];

const TONE = { New: "bg-neutral-100 text-neutral-600", Booked: "bg-neutral-200 text-neutral-700", Converted: "bg-[#111111] text-white" };

export default function CrmPage() {
  useSeo("CRM — Savy Kerno", "Savy Kerno's built-in CRM organises every enquiry: leads, follow-ups and appointments in one place, so no customer conversation slips through.");
  const [sel, setSel] = useState(0);
  const lead = SAMPLE_LEADS[sel];
  return (
    <PublicShell
      testid="crm-page"
      eyebrow="CRM"
      title="Leads that never slip through"
      sub="Every enquiry lands in one private workspace — searchable, filterable, and linked to appointments, tasks and campaigns. Import your existing contacts in minutes with the CSV importer."
    >
      <Block title="A pipeline you can read at a glance" testid="crm-pipeline">
        <div className="rounded-2xl border border-neutral-200 bg-neutral-50/70 p-5">
          <p className="text-sm font-bold">Sample pipeline<SampleNote /></p>
          <div className="mt-4 space-y-2" data-testid="crm-sample-list">
            {SAMPLE_LEADS.map((l, i) => (
              <button
                key={l.name}
                onClick={() => setSel(i)}
                data-testid={`crm-sample-lead-${i}`}
                aria-pressed={sel === i}
                className={`flex w-full items-center justify-between rounded-xl border px-4 py-2.5 text-left text-sm transition-colors ${sel === i ? "border-[#111111] bg-white" : "border-neutral-200 bg-white/60 hover:border-neutral-300"}`}
              >
                <span className="font-semibold text-neutral-800">{l.name}</span>
                <span className="hidden text-xs text-neutral-400 sm:inline">{l.source}</span>
                <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${TONE[l.status]}`}>{l.status}</span>
              </button>
            ))}
          </div>
          <div className="mt-4 rounded-xl border border-neutral-200 bg-white p-4 text-sm" data-testid="crm-sample-detail">
            <p className="font-semibold">{lead.name} <span className="text-xs font-normal text-neutral-400">· {lead.source}</span></p>
            <p className="mt-1 text-xs text-neutral-600">{lead.note}</p>
            <p className={`mt-2 text-[11px] font-semibold ${lead.consent === "Do not call" ? "text-red-600" : "text-neutral-500"}`}>
              {lead.consent}
            </p>
          </div>
        </div>
      </Block>

      <Block title="Built for real workflows" testid="crm-workflows">
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            ["Bulk CSV import", "Upload, map columns, preview and import — duplicates skipped, batches labelled, consent never assumed."],
            ["Appointments", "Book, reschedule and cancel with conflict protection, buffers and past-date blocking."],
            ["Follow-up tasks", "A follow-up date on a lead becomes a task automatically, so nobody is forgotten."],
            ["Customer self-service", "Share a secure manage link so customers can move or cancel their own booking within your notice rules."],
          ].map(([t, d]) => (
            <div key={t} className="rounded-2xl border border-neutral-200 p-5">
              <p className="text-sm font-bold">{t}</p>
              <p className="mt-1.5 text-xs leading-relaxed text-neutral-500">{d}</p>
            </div>
          ))}
        </div>
      </Block>

      <Block title="Private by construction" testid="crm-privacy">
        <p className="text-sm leading-relaxed text-neutral-600">
          Your CRM is scoped to your account on every request — other businesses cannot see, search or export your
          records, and consent and do-not-call markers travel with each contact everywhere they're used.
        </p>
      </Block>
    </PublicShell>
  );
}
